import React, { useState, useCallback } from 'react';
import { IconButton, Tooltip, Collapse } from '@mui/material';
import MessageIcon from '@mui/icons-material/Message';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ForumIcon from '@mui/icons-material/Forum';
import InfoIcon from '@mui/icons-material/Info';
import CancelIcon from '@mui/icons-material/Cancel';
import '../../styles/Button.css';
import { ThreadData } from '../../api/types';

type BotAnalyticsProps = {
  botName: string;
  type: string;
  focus: string;
  enabled: boolean;
  activeThreads: number;
  inactiveThreads: number;
  userMessages: number;
  threads: ThreadData[];
};

const formatDate = (ts: number) => new Date(ts).toLocaleString();

const BotAnalytics: React.FC<BotAnalyticsProps> = ({
  botName,
  type,
  focus,
  enabled,
  activeThreads,
  inactiveThreads,
  userMessages,
  threads,
}) => {
  const [openThreads, setOpenThreads] = useState<Record<string, boolean>>({});

  const toggleThread = useCallback((id: string) => {
    setOpenThreads((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  return (
    <div style={{
      height: '100%',
      width: '100%',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'Montserrat',
      padding: '16px',
      borderRadius: '12px',
      backgroundColor: '#fff',
    }}>
      {/* Bot name */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', marginTop: '60px' }}>
        {enabled ? null : 
          <Tooltip title="Bot is disabled due to billing, go to Account to view">
            <CancelIcon style={{ color: '#e74c3c', verticalAlign: 'middle', cursor: 'help', marginRight: '5px' }} />
          </Tooltip>}
        <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 600 }}>Assistant: {botName}</h2>
      </div>
      {/* <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px', marginTop: '60px' }}>
          <h3>Type: {type} Focus: {focus}</h3>
      </div> */}
      <div style={{ fontSize: '18px', color: '#666', display: 'flex', alignItems: 'center', marginBottom: '24px', marginTop: '10px' }}>
                Type: {type} · Focus: {focus}
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{ textAlign: 'center' }}>
          <ForumIcon style={{ color: 'var(--primary)', fontSize: '32px' }} />
          <div style={{ fontSize: '20px', fontWeight: 600 }}>{inactiveThreads + activeThreads}</div>
          <div style={{ color: '#666' }}>Total Threads</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <InfoIcon style={{ color: '#2ecc71', fontSize: '32px' }} />
          <div style={{ fontSize: '20px', fontWeight: 600 }}>{activeThreads} / {inactiveThreads + activeThreads}</div>
          <div style={{ color: '#666' }}>Active / Total</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <MessageIcon style={{ color: '#00bcd4', fontSize: '32px' }} />
          <div style={{ fontSize: '20px', fontWeight: 600 }}>{userMessages}</div>
          <div style={{ color: '#666' }}>User Messages</div>
        </div>
      </div>

      {/* Scrollable thread list */}
      <div className="typer-scroll-container" style={{ overflowY: 'auto', flex: 1, paddingRight: '6px' }}>
        {threads.map((thread) => (
          <div key={thread.threadId} style={{
            border: '1px solid #e0e0e0',
            borderRadius: '16px',
            padding: '16px',
            marginBottom: '16px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
            backgroundColor: '#fafafa'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontFamily: 'Montserrat',
              fontSize: '16px',
              fontWeight: 500,
            }}>
              <div>
                Thread ID: <span style={{ color: 'var(--primary)' }}>{thread.threadId}</span>
                <div style={{ fontSize: '13px', color: '#999' }}>Created: {formatDate(thread.createdAt)}</div>
              </div>
              <Tooltip title={openThreads[thread.threadId] ? 'Hide Messages' : 'Show Messages'}>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleThread(thread.threadId);
                  }}
                  size="large"
                >
                  {openThreads[thread.threadId]
                    ? <ExpandLessIcon style={{ color: 'var(--primary)' }} />
                    : <ExpandMoreIcon style={{ color: 'var(--primary)' }} />}
                </IconButton>
              </Tooltip>
            </div>

            <Collapse in={openThreads[thread.threadId]} timeout={200} unmountOnExit>
  <div className="typer-scroll-container" style={{
    marginTop: '16px',
    maxHeight: '300px',
    overflowY: 'auto',
    paddingRight: '6px'
  }}>
    {thread.messages.length === 0 ? (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#888',
        fontStyle: 'italic',
        fontSize: '15px',
        padding: '32px 0'
      }}>
        <span style={{ fontSize: '24px' }}>✉️</span>
        <div>No messages in this thread</div>
      </div>
    ) : (
      thread.messages.map((msg, idx) => (
        <div key={idx} style={{
          marginBottom: '8px',
          padding: '10px',
          borderRadius: '12px',
          backgroundColor: msg.sender === 'user' ? '#f0f0f0' : 'var(--message)',
          color: '#222',
          fontSize: '14px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          textAlign: 'left',
        }}>
          <div style={{ fontWeight: 500, marginBottom: '4px' }}>
            {msg.sender === 'user' ? 'User' : 'AI'} · {formatDate(msg.createdAt)}
          </div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
        </div>
      ))
    )}
  </div>
</Collapse>

          </div>
        ))}
      </div>
    </div>
  );
};

export default BotAnalytics;
